export function fmtDur(hours) {
  // Round to whole minutes first, then split — otherwise float values like
  // 0.9999999999999999 (from 5-min snapping) render as "60m" / "1h 60m".
  const totalMinutes = Math.round((Number(hours) || 0) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (!h) return `${m}m`;
  if (!m) return `${h}h`;
  return `${h}h ${m}m`;
}

export function fmtHM(hours) {
  const total = Math.round(Math.max(0, Number(hours) || 0) * 60);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function fmtTime(h) {
  const totalMinutes = Math.round(h * 60);
  const hour = Math.floor(totalMinutes / 60);
  const min = totalMinutes % 60;
  return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

export function parseTime(value) {
  const [h, m] = value.split(':').map(Number);
  return h + (m || 0) / 60;
}

// Parse a typed duration into decimal hours. Accepts the "H:MM" clock form
// ("1:30" -> 1.5), a decimal number of hours ("1.5" -> 1.5), and compact digit
// entry that mirrors the From/To fields, where the trailing two digits are
// minutes ("0015"/"015" -> 15m, "130" -> 1h 30m, "8" -> 8h). Returns null for
// invalid input.
export function parseDuration(value) {
  const text = String(value).trim();
  if (!text) return null;

  if (text.includes(':')) {
    const parts = text.split(':');
    if (parts.length > 2) return null;
    const h = Number(parts[0]);
    const m = Number(parts[1] || 0);
    if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || m < 0 || m > 59)
      return null;
    return h + m / 60;
  }

  // A value with a decimal point is a plain number of hours.
  if (text.includes('.')) {
    const h = Number(text);
    return Number.isFinite(h) && h >= 0 ? h : null;
  }

  // Compact digit entry: the trailing two digits are minutes once there are 3+
  // digits; one or two digits are read as whole hours.
  if (!/^\d+$/.test(text)) return null;
  const h = text.length <= 2 ? Number(text) : Number(text.slice(0, -2));
  const m = text.length <= 2 ? 0 : Number(text.slice(-2));
  if (m > 59) return null;
  return h + m / 60;
}

export function mondayOf(date) {
  const out = new Date(date);
  const day = out.getDay() || 7;
  out.setDate(out.getDate() - day + 1);
  out.setHours(0, 0, 0, 0);
  return out;
}

export function isoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function addDays(date, days) {
  const out = new Date(date);
  out.setDate(out.getDate() + days);
  return out;
}

// Parse a YYYY-MM-DD string as a local date (new Date('YYYY-MM-DD') would be
// UTC midnight, which shifts a day in western timezones).
export function parseIsoDate(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// Which week (Monday) a member calendar should open on for a report range:
// the current week while today falls inside the range, otherwise the week
// containing the range start — so "Last week" opens on last week instead of
// an empty current week.
export function calendarStartMonday(from, to, today = new Date()) {
  const iso = isoDate(today);
  if (from && to && (iso < from || iso > to))
    return mondayOf(parseIsoDate(from));
  return mondayOf(today);
}

export function entryHours(entry) {
  return Math.max(0, entry.end - entry.start);
}

export function byWeek(entries, weekStart) {
  const start = isoDate(weekStart);
  const end = isoDate(addDays(weekStart, 7));
  return entries.filter(
    (entry) => entry.workDate >= start && entry.workDate < end,
  );
}
